// One line's selected quantity on the RBP Shopping List. A step shows at once (SL1.0's optimistic
// `productRowUpdated`, `rbp_npr.js:78-121`) and sends the ABSOLUTE quantity now shown, so the last
// write wins on the server as SL1.0's did. At most one write is in flight per line: a step while one
// is in flight records the latest wanted quantity and sends it when the first settles, so quick
// clicks cost at most two writes and the last wanted value is the one stored; a click is never
// dropped. A refused or failed write puts the last stored quantity back and keeps the reason for the
// line to show until the next step. When the line unmounts (its group collapses, as a group
// deselect does), a queued write is dropped and nothing more is reported. An already in-flight
// write cannot be unsent, so the page lets the tracked writes settle (`RbpPendingSelections`)
// before a Deselect's DELETE, and this hook registers each flush run with that tracker.
//
// A typed quantity (`set`) is the same write as a step, to an absolute value clamped to 0 and the
// line maximum: typing 100 is one write, not a hundred steps (ADO-4037).

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  RBP_SELECTION_MAX_QUANTITY,
  RbpRequestError,
  setRbpSelection,
  type RbpSelection,
} from '../api/rbp.client';
import type { RbpPendingSelections } from './rbpPendingSelections';

export interface RbpSelectionControl {
  /** The quantity the line shows: the wanted one while a write is on its way. */
  readonly quantity: number;
  /** A write is in flight. */
  readonly busy: boolean;
  /** A group or all Deselect is clearing: the line is frozen until it completes or fails. */
  readonly clearing: boolean;
  /** Why the last write was not stored; null when it was, or after the next step. */
  readonly error: string | null;
  readonly step: (delta: 1 | -1) => void;
  /** Sets a typed quantity, clamped to 0 and the line maximum; the same write as a step. */
  readonly set: (quantity: number) => void;
}

const GENERIC_FAILURE = 'We couldn’t save this quantity. Please try again.';

const noopSubscribe = () => () => undefined;
const notClearing = () => false;

function failureMessage(error: unknown): string {
  // A 400 or 409 carries the server's own reason (the line's state, the quantity's rule).
  return error instanceof RbpRequestError &&
    (error.status === 400 || error.status === 409) &&
    error.message !== ''
    ? error.message
    : GENERIC_FAILURE;
}

export function useRbpSelection(
  benefitGroupCode: string,
  productCode: string | null,
  storedQuantity: number,
  onSaved?: (selection: RbpSelection) => void,
  pending?: RbpPendingSelections,
): RbpSelectionControl {
  const [quantity, setQuantity] = useState(storedQuantity);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stored = useRef(storedQuantity);
  const shown = useRef(storedQuantity);
  const wanted = useRef<number | null>(null);
  const inFlight = useRef(false);
  const saved = useRef(onSaved);
  saved.current = onSaved;
  const pendingWrites = useRef(pending);
  pendingWrites.current = pending;
  const mounted = useRef(true);

  // The clear latch, reactive so the steppers disable while a Deselect clears, and released when it
  // completes or fails. The synchronous guard in `step` (below) reads the ref, not this, so a step
  // in the same tick as the Deselect is blocked before any render.
  const clearing = useSyncExternalStore(
    pending?.subscribe ?? noopSubscribe,
    pending?.isClearing ?? notClearing,
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      wanted.current = null;
    };
  }, []);

  // A re-read of the lines answers a new stored quantity: show it, unless a write is on its way.
  useEffect(() => {
    if (inFlight.current) return;
    stored.current = storedQuantity;
    shown.current = storedQuantity;
    setQuantity(storedQuantity);
  }, [storedQuantity]);

  const flush = useCallback(async () => {
    if (productCode === null) return;
    inFlight.current = true;
    setBusy(true);
    while (wanted.current !== null) {
      const target = wanted.current;
      wanted.current = null;
      try {
        const selection = await setRbpSelection(benefitGroupCode, productCode, target);
        if (!mounted.current) return;
        stored.current = selection.selectedQuantity;
        if (wanted.current === null) {
          shown.current = selection.selectedQuantity;
          setQuantity(selection.selectedQuantity);
        }
        saved.current?.(selection);
      } catch (failure: unknown) {
        if (!mounted.current) return;
        wanted.current = null;
        shown.current = stored.current;
        setQuantity(stored.current);
        setError(failureMessage(failure));
      }
    }
    inFlight.current = false;
    setBusy(false);
  }, [benefitGroupCode, productCode]);

  const change = useCallback(
    (target: number) => {
      if (productCode === null) return;
      // A Deselect is clearing these lines: a change must not start a write that could land after
      // the DELETE. Read synchronously, so a click in the same tick as the Deselect is blocked too.
      if (pendingWrites.current?.isClearing() === true) return;
      const next = Math.min(RBP_SELECTION_MAX_QUANTITY, Math.max(0, target));
      if (next === shown.current) return;
      shown.current = next;
      setQuantity(next);
      setError(null);
      wanted.current = next;
      if (!inFlight.current) {
        const running = flush();
        void running;
        pendingWrites.current?.track(running);
      }
    },
    [flush, productCode],
  );
  const step = useCallback((delta: 1 | -1) => change(shown.current + delta), [change]);

  return { quantity, busy, clearing, error, step, set: change };
}
